import { FinancialUnavailable, isCurrentAssessment, type FinancialResponseAvailability } from '../financial-unavailable';
/**
 * Deal Serviceability Panel (SPRINT-6B F001)
 *
 * Displays/manages deal serviceability configuration and calculations.
 * Shows:
 * - Rate configuration (approach, customer rate, assessment rate, buffer)
 * - Loan details (amount, term, repayment type)
 * - Lender requirements (min DSCR, min ICR)
 * - Calculated results (ICR, DSCR, pass/fail)
 *
 * States:
 * - Empty: No config exists, show "Configure Serviceability" button
 * - Display: Show read-only config with Edit and Calculate buttons
 * - Edit: Form to modify configuration
 * - Calculating: Loading state during calculation API call
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Loader2, Calculator } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
// Native selects used for Playwright test compatibility (selectOption requires <select> element)
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// Types
interface DealServiceabilityConfig {
  id: string;
  dealId: string;
  serviceabilityApproach: 'customer_rate' | 'assessment_rate' | 'customer_rate_plus_buffer' | 'higher_of';
  customerRate: number;
  assessmentRate: number | null;
  bufferRate: number | null;
  loanAmount: number;
  loanTermMonths: number;
  repaymentType: 'pi' | 'io' | null;
  incomeSource: 'business' | 'combined' | 'personal' | null;
  lenderMinDscr: number | null;
  lenderMinIcr: number | null;
  servicingNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

interface CalculationResult extends FinancialResponseAvailability {
  rateUsed: number | null;
  monthlyRepayment: number | null;
  annualRepayment: number | null;
  totalAvailableIncome: number | null;
  totalExistingCommitments: number | null;
  proposedCommitment: number | null;
  totalCommitments: number | null;
  dscr: number | null;
  icr: number | null;
  passesServicing: boolean | null;
  lenderMinDscr: number | null;
  lenderMinIcr: number | null;
}

interface FormState {
  serviceabilityApproach: 'customer_rate' | 'assessment_rate' | 'customer_rate_plus_buffer' | 'higher_of';
  customerRate: string;
  assessmentRate: string;
  bufferRate: string;
  loanAmount: string;
  loanTermMonths: string;
  repaymentType: 'pi' | 'io';
  incomeSource: 'business' | 'combined' | 'personal';
  lenderMinDscr: string;
  lenderMinIcr: string;
  servicingNotes: string;
}

export interface DealServiceabilityPanelProps {
  dealId: string;
}

// Label maps
const APPROACH_LABELS: Record<string, string> = {
  customer_rate: 'Customer Rate',
  assessment_rate: 'Assessment Rate',
  customer_rate_plus_buffer: 'Customer Rate + Buffer',
  higher_of: 'Higher Of',
};

const REPAYMENT_TYPE_LABELS: Record<string, string> = {
  pi: 'P&I (Principal & Interest)',
  io: 'Interest Only',
};

const INCOME_SOURCE_LABELS: Record<string, string> = {
  business: 'Business',
  combined: 'Combined',
  personal: 'Personal',
};

// Helper: Determine status badge based on ratio vs minimum
function getStatusBadge(ratio: number, minimum: number | null): { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' } {
  if (!minimum) return { label: 'N/A', variant: 'outline' };

  const coverage = ratio / minimum;
  if (coverage >= 1.5) return { label: 'Strong', variant: 'default' }; // Green
  if (coverage >= 1.1) return { label: 'Adequate', variant: 'secondary' }; // Blue
  if (coverage >= 0.95) return { label: 'Marginal', variant: 'outline' }; // Amber/warning
  return { label: 'Weak', variant: 'destructive' }; // Red
}

// Convert percentage display (6.5) to decimal (0.065)
function percentToDecimal(percent: string): number {
  const num = parseFloat(percent);
  return isNaN(num) ? 0 : num / 100;
}

// Convert decimal (0.065) to percentage display (6.5)
function decimalToPercent(decimal: number | null | undefined): string {
  if (decimal === null || decimal === undefined) return '';
  return (decimal * 100).toFixed(2);
}

// Format currency
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function ServiceabilityCalculationResult({ calculationResult, failed = false, dealId }: { calculationResult: CalculationResult | null; failed?: boolean; dealId?: string }) {
  if (failed || !calculationResult || !isCurrentAssessment(calculationResult) || calculationResult.passesServicing == null || calculationResult.dscr == null || calculationResult.icr == null || !Number.isFinite(Number(calculationResult.dscr)) || !Number.isFinite(Number(calculationResult.icr))) {
    return <FinancialUnavailable dealId={dealId} value={failed ? { assessmentStatus: 'failed', historical: calculationResult } : calculationResult} action="Review financial inputs and lender policy, then use Calculate." />;
  }
  return (
    <div data-testid="serviceability-results" className="border-t pt-4 mt-4">
      <h4 className="text-xs font-semibold text-muted-foreground mb-2">CALCULATION RESULTS</h4>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className="text-muted-foreground">Rate Used</dt>
          <dd className="font-medium">{calculationResult.rateUsed == null ? 'Not assessed' : `${(calculationResult.rateUsed * 100).toFixed(2)}%`}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Monthly Repayment</dt>
          <dd className="font-medium">{calculationResult.monthlyRepayment == null ? 'Not assessed' : formatCurrency(calculationResult.monthlyRepayment)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Annual Repayment</dt>
          <dd className="font-medium">{calculationResult.annualRepayment == null ? 'Not assessed' : formatCurrency(calculationResult.annualRepayment)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">DSCR (Debt Service Coverage Ratio)</dt>
          <dd className="flex items-center gap-2">
            <span className="font-medium">{Number(calculationResult.dscr).toFixed(2)}x</span>
            <Badge
              variant={getStatusBadge(calculationResult.dscr, calculationResult.lenderMinDscr).variant}
              data-testid="dscr-status-badge"
            >
              {getStatusBadge(calculationResult.dscr, calculationResult.lenderMinDscr).label}
            </Badge>
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">ICR (Interest Coverage Ratio)</dt>
          <dd className="flex items-center gap-2">
            <span className="font-medium">{Number(calculationResult.icr).toFixed(2)}x</span>
            <Badge
              variant={getStatusBadge(calculationResult.icr, calculationResult.lenderMinIcr).variant}
              data-testid="icr-status-badge"
            >
              {getStatusBadge(calculationResult.icr, calculationResult.lenderMinIcr).label}
            </Badge>
          </dd>
        </div>
        <div className="col-span-2">
          <dt className="text-muted-foreground">Servicing Status</dt>
          <dd className="flex items-center gap-2 mt-1">
            <Badge
              variant={calculationResult.passesServicing ? 'default' : 'destructive'}
              data-testid="servicing-status-badge"
            >
              {calculationResult.passesServicing ? 'Passes' : 'Fails'}
            </Badge>
          </dd>
        </div>
      </dl>
    </div>
  );
}

const ScopedDealServiceabilityPanel: React.FC<DealServiceabilityPanelProps> = ({ dealId }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [calculationResult, setCalculationResult] = useState<CalculationResult | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Default form state
  const defaultFormState: FormState = {
    serviceabilityApproach: 'customer_rate',
    customerRate: '',
    assessmentRate: '',
    bufferRate: '',
    loanAmount: '',
    loanTermMonths: '',
    repaymentType: 'pi',
    incomeSource: 'business',
    lenderMinDscr: '',
    lenderMinIcr: '',
    servicingNotes: '',
  };

  const [formState, setFormState] = useState<FormState>(defaultFormState);

  // Fetch serviceability config
  const { data: config, isLoading, error } = useQuery<DealServiceabilityConfig>({
    queryKey: [`/api/crm/deals/${dealId}/serviceability`],
    enabled: !!dealId,
    retry: false, // 404 is expected when no config exists
  });

  // Check if 404 (no config) vs actual error
  const hasConfig = !!config && !error;
  const is404 = error && (error as any)?.message?.includes('404');

  // Sync form state when config loads
  useEffect(() => {
    if (config) {
      setFormState({
        serviceabilityApproach: config.serviceabilityApproach,
        customerRate: decimalToPercent(config.customerRate),
        assessmentRate: decimalToPercent(config.assessmentRate),
        bufferRate: decimalToPercent(config.bufferRate),
        loanAmount: config.loanAmount?.toString() || '',
        loanTermMonths: config.loanTermMonths?.toString() || '',
        repaymentType: config.repaymentType || 'pi',
        incomeSource: config.incomeSource || 'business',
        lenderMinDscr: config.lenderMinDscr?.toString() || '',
        lenderMinIcr: config.lenderMinIcr?.toString() || '',
        servicingNotes: config.servicingNotes || '',
      });
    }
  }, [config]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('POST', `/api/crm/deals/${dealId}/serviceability`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/serviceability`] });
      setIsModalOpen(false);
      setIsEditMode(false);
      toast({
        title: 'Serviceability configuration created',
        description: 'You can now calculate serviceability metrics',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create configuration',
        variant: 'destructive',
      });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('PATCH', `/api/crm/deals/${dealId}/serviceability`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/serviceability`] });
      setIsEditMode(false);
      toast({
        title: 'Configuration updated',
        description: 'Serviceability configuration has been saved',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update configuration',
        variant: 'destructive',
      });
    },
  });

  // Calculate mutation
  const calculateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/financial/calculate-serviceability', { dealId });
      return res.json() as Promise<CalculationResult>;
    },
    onSuccess: (data) => {
      setCalculationResult(data);
      toast({
        title: isCurrentAssessment(data) ? 'Serviceability calculated' : 'Not assessed',
        description: isCurrentAssessment(data) ? 'Calculation results updated' : 'Review the financial evidence and lender policy.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to calculate serviceability',
        variant: 'destructive',
      });
    },
  });

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formState.serviceabilityApproach) {
      errors.serviceabilityApproach = 'Serviceability approach is required';
    }
    if (!formState.customerRate || isNaN(parseFloat(formState.customerRate))) {
      errors.customerRate = 'Customer rate is required';
    }
    if (!formState.loanAmount || isNaN(parseFloat(formState.loanAmount))) {
      errors.loanAmount = 'Loan amount is required';
    }
    if (!formState.loanTermMonths || isNaN(parseInt(formState.loanTermMonths, 10))) {
      errors.loanTermMonths = 'Loan term is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle save (create or update)
  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = {
      serviceabilityApproach: formState.serviceabilityApproach,
      customerRate: percentToDecimal(formState.customerRate),
      assessmentRate: formState.assessmentRate ? percentToDecimal(formState.assessmentRate) : null,
      bufferRate: formState.bufferRate ? percentToDecimal(formState.bufferRate) : null,
      loanAmount: parseFloat(formState.loanAmount),
      loanTermMonths: parseInt(formState.loanTermMonths, 10),
      repaymentType: formState.repaymentType,
      incomeSource: formState.incomeSource,
      lenderMinDscr: formState.lenderMinDscr ? parseFloat(formState.lenderMinDscr) : null,
      lenderMinIcr: formState.lenderMinIcr ? parseFloat(formState.lenderMinIcr) : null,
      servicingNotes: formState.servicingNotes || null,
    };

    if (hasConfig) {
      await updateMutation.mutateAsync(payload);
    } else {
      await createMutation.mutateAsync(payload);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setFormErrors({});
    if (config) {
      // Reset to saved values
      setFormState({
        serviceabilityApproach: config.serviceabilityApproach,
        customerRate: decimalToPercent(config.customerRate),
        assessmentRate: decimalToPercent(config.assessmentRate),
        bufferRate: decimalToPercent(config.bufferRate),
        loanAmount: config.loanAmount?.toString() || '',
        loanTermMonths: config.loanTermMonths?.toString() || '',
        repaymentType: config.repaymentType || 'pi',
        incomeSource: config.incomeSource || 'business',
        lenderMinDscr: config.lenderMinDscr?.toString() || '',
        lenderMinIcr: config.lenderMinIcr?.toString() || '',
        servicingNotes: config.servicingNotes || '',
      });
    } else {
      setFormState(defaultFormState);
    }
    setIsEditMode(false);
    setIsModalOpen(false);
  };

  // Open modal for new config
  const handleOpenConfigureModal = () => {
    setFormState(defaultFormState);
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Loading state
  if (isLoading) {
    return (
      <Card data-testid="deal-serviceability-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-48" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i}>
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-5 w-32" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state - no config exists
  if (!hasConfig || is404) {
    return (
      <>
        <Card data-testid="deal-serviceability-panel">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Serviceability</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-muted-foreground mb-4">No serviceability configuration for this deal</p>
            <p className="text-xs text-muted-foreground mb-6">
              Configure serviceability parameters to assess loan capacity
            </p>
            <Button onClick={handleOpenConfigureModal}>
              <Plus className="h-4 w-4 mr-2" />
              Configure Serviceability
            </Button>
          </CardContent>
        </Card>

        {/* Configure Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Configure Serviceability</DialogTitle>
              <DialogDescription>
                Set up serviceability calculation parameters for this deal
              </DialogDescription>
            </DialogHeader>
            <ServiceabilityForm
              formState={formState}
              setFormState={setFormState}
              formErrors={formErrors}
              isNew={true}
            />
            <DialogFooter>
              <Button variant="outline" onClick={handleCancel} disabled={createMutation.isPending}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Display mode with config
  return (
    <>
      <Card data-testid="deal-serviceability-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Serviceability</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => calculateMutation.mutate()}
                disabled={calculateMutation.isPending}
              >
                {calculateMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Calculator className="h-4 w-4 mr-1" />
                )}
                Calculate
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditMode(true)}
              >
                <Edit className="h-4 w-4 mr-1" />
                Edit
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Configuration Summary */}
          <div className="space-y-4">
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">RATE CONFIGURATION</h4>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Approach</dt>
                  <dd className="font-medium">{APPROACH_LABELS[config.serviceabilityApproach]}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Customer Rate</dt>
                  <dd className="font-medium">{decimalToPercent(config.customerRate)}%</dd>
                </div>
                {config.assessmentRate && (
                  <div>
                    <dt className="text-muted-foreground">Assessment Rate</dt>
                    <dd className="font-medium">{decimalToPercent(config.assessmentRate)}%</dd>
                  </div>
                )}
                {config.bufferRate && (
                  <div>
                    <dt className="text-muted-foreground">Buffer Rate</dt>
                    <dd className="font-medium">{decimalToPercent(config.bufferRate)}%</dd>
                  </div>
                )}
              </dl>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-muted-foreground mb-2">LOAN DETAILS</h4>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Loan Amount</dt>
                  <dd className="font-medium">{formatCurrency(config.loanAmount)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Term</dt>
                  <dd className="font-medium">{config.loanTermMonths} months</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Repayment Type</dt>
                  <dd className="font-medium">{REPAYMENT_TYPE_LABELS[config.repaymentType || 'pi']}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Income Source</dt>
                  <dd className="font-medium">{INCOME_SOURCE_LABELS[config.incomeSource || 'business']}</dd>
                </div>
              </dl>
            </div>

            {(config.lenderMinDscr || config.lenderMinIcr) && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground mb-2">LENDER REQUIREMENTS</h4>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  {config.lenderMinDscr && (
                    <div>
                      <dt className="text-muted-foreground">Min DSCR</dt>
                      <dd className="font-medium">{config.lenderMinDscr}x</dd>
                    </div>
                  )}
                  {config.lenderMinIcr && (
                    <div>
                      <dt className="text-muted-foreground">Min ICR</dt>
                      <dd className="font-medium">{config.lenderMinIcr}x</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Calculation Results Section */}
            {(calculationResult || calculateMutation.isError) && <ServiceabilityCalculationResult dealId={dealId} calculationResult={calculationResult} failed={calculateMutation.isError} />}

            {config.servicingNotes && (
              <div className="border-t pt-4 mt-4">
                <h4 className="text-xs font-semibold text-muted-foreground mb-2">NOTES</h4>
                <p className="text-sm">{config.servicingNotes}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={isEditMode} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Serviceability Configuration</DialogTitle>
            <DialogDescription>
              Modify serviceability calculation parameters
            </DialogDescription>
          </DialogHeader>
          <ServiceabilityForm
            formState={formState}
            setFormState={setFormState}
            formErrors={formErrors}
            isNew={false}
          />
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel} disabled={updateMutation.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Form component (shared between create and edit)
interface ServiceabilityFormProps {
  formState: FormState;
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  formErrors: Record<string, string>;
  isNew: boolean;
}

const ServiceabilityForm: React.FC<ServiceabilityFormProps> = ({
  formState,
  setFormState,
  formErrors,
}) => {
  return (
    <div className="space-y-4 py-4">
      {/* Serviceability Approach - using native select for test compatibility */}
      <div className="space-y-2">
        <Label htmlFor="serviceabilityApproach">Serviceability Approach *</Label>
        <select
          id="serviceabilityApproach"
          aria-label="Serviceability Approach"
          value={formState.serviceabilityApproach}
          onChange={(e) =>
            setFormState((prev) => ({ ...prev, serviceabilityApproach: e.target.value as FormState['serviceabilityApproach'] }))
          }
          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="customer_rate">Customer Rate</option>
          <option value="assessment_rate">Assessment Rate</option>
          <option value="customer_rate_plus_buffer">Customer Rate + Buffer</option>
          <option value="higher_of">Higher Of</option>
        </select>
        {formErrors.serviceabilityApproach && (
          <p className="text-sm text-destructive" data-testid="serviceabilityApproach-error">
            {formErrors.serviceabilityApproach}
          </p>
        )}
      </div>

      {/* Customer Rate */}
      <div className="space-y-2">
        <Label htmlFor="customerRate">Customer Rate (%) *</Label>
        <Input
          id="customerRate"
          type="number"
          step="0.01"
          min="0"
          max="100"
          value={formState.customerRate}
          onChange={(e) => setFormState((prev) => ({ ...prev, customerRate: e.target.value }))}
          placeholder="e.g., 6.5"
          aria-label="Customer Rate"
        />
        {formErrors.customerRate && (
          <p className="text-sm text-destructive" data-testid="customerRate-error">
            {formErrors.customerRate}
          </p>
        )}
      </div>

      {/* Assessment Rate (conditional) */}
      {(formState.serviceabilityApproach === 'assessment_rate' ||
        formState.serviceabilityApproach === 'higher_of') && (
        <div className="space-y-2">
          <Label htmlFor="assessmentRate">Assessment Rate (%)</Label>
          <Input
            id="assessmentRate"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={formState.assessmentRate}
            onChange={(e) => setFormState((prev) => ({ ...prev, assessmentRate: e.target.value }))}
            placeholder="e.g., 8.0"
            aria-label="Assessment Rate"
          />
        </div>
      )}

      {/* Buffer Rate (conditional) */}
      {formState.serviceabilityApproach === 'customer_rate_plus_buffer' && (
        <div className="space-y-2">
          <Label htmlFor="bufferRate">Buffer Rate (%)</Label>
          <Input
            id="bufferRate"
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={formState.bufferRate}
            onChange={(e) => setFormState((prev) => ({ ...prev, bufferRate: e.target.value }))}
            placeholder="e.g., 2.0"
            aria-label="Buffer Rate"
          />
        </div>
      )}

      {/* Loan Amount */}
      <div className="space-y-2">
        <Label htmlFor="loanAmount">Loan Amount ($) *</Label>
        <Input
          id="loanAmount"
          type="number"
          step="1000"
          min="0"
          value={formState.loanAmount}
          onChange={(e) => setFormState((prev) => ({ ...prev, loanAmount: e.target.value }))}
          placeholder="e.g., 1000000"
          aria-label="Loan Amount"
        />
        {formErrors.loanAmount && (
          <p className="text-sm text-destructive" data-testid="loanAmount-error">
            {formErrors.loanAmount}
          </p>
        )}
      </div>

      {/* Loan Term */}
      <div className="space-y-2">
        <Label htmlFor="loanTermMonths">Loan Term (months) *</Label>
        <Input
          id="loanTermMonths"
          type="number"
          step="1"
          min="1"
          max="600"
          value={formState.loanTermMonths}
          onChange={(e) => setFormState((prev) => ({ ...prev, loanTermMonths: e.target.value }))}
          placeholder="e.g., 300"
          aria-label="Loan Term Months"
        />
        {formErrors.loanTermMonths && (
          <p className="text-sm text-destructive" data-testid="loanTermMonths-error">
            {formErrors.loanTermMonths}
          </p>
        )}
      </div>

      {/* Repayment Type - using native select for test compatibility */}
      <div className="space-y-2">
        <Label htmlFor="repaymentType">Repayment Type</Label>
        <select
          id="repaymentType"
          aria-label="Repayment Type"
          value={formState.repaymentType}
          onChange={(e) =>
            setFormState((prev) => ({ ...prev, repaymentType: e.target.value as FormState['repaymentType'] }))
          }
          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="pi">P&I (Principal & Interest)</option>
          <option value="io">Interest Only</option>
        </select>
      </div>

      {/* Income Source - using native select for test compatibility */}
      <div className="space-y-2">
        <Label htmlFor="incomeSource">Income Source</Label>
        <select
          id="incomeSource"
          aria-label="Income Source"
          value={formState.incomeSource}
          onChange={(e) =>
            setFormState((prev) => ({ ...prev, incomeSource: e.target.value as FormState['incomeSource'] }))
          }
          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="business">Business</option>
          <option value="combined">Combined</option>
          <option value="personal">Personal</option>
        </select>
      </div>

      {/* Lender Min DSCR */}
      <div className="space-y-2">
        <Label htmlFor="lenderMinDscr">Lender Min DSCR (x)</Label>
        <Input
          id="lenderMinDscr"
          type="number"
          step="0.01"
          min="0"
          value={formState.lenderMinDscr}
          onChange={(e) => setFormState((prev) => ({ ...prev, lenderMinDscr: e.target.value }))}
          placeholder="e.g., 1.25"
        />
      </div>

      {/* Lender Min ICR */}
      <div className="space-y-2">
        <Label htmlFor="lenderMinIcr">Lender Min ICR (x)</Label>
        <Input
          id="lenderMinIcr"
          type="number"
          step="0.01"
          min="0"
          value={formState.lenderMinIcr}
          onChange={(e) => setFormState((prev) => ({ ...prev, lenderMinIcr: e.target.value }))}
          placeholder="e.g., 1.5"
        />
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="servicingNotes">Notes</Label>
        <Textarea
          id="servicingNotes"
          value={formState.servicingNotes}
          onChange={(e) => setFormState((prev) => ({ ...prev, servicingNotes: e.target.value }))}
          placeholder="Enter notes (optional)"
          rows={3}
        />
      </div>
    </div>
  );
};

export const DealServiceabilityPanel: React.FC<DealServiceabilityPanelProps> = props => <ScopedDealServiceabilityPanel key={props.dealId} {...props} />;

export default DealServiceabilityPanel;
